import React from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Users, Globe, Zap, Shield, ArrowRight } from 'lucide-react';

// User provided bull bear image for about section
const BULL_BEAR_IMAGE = "https://customer-assets.emergentagent.com/job_f7918f01-bf20-4cfe-88f8-06dc16353ee9/artifacts/yxc1usje_Bull%20Bear%20Images%20%E2%80%93%20Browse%2020%2C692%20Stock%20Photos%2C%20Vectors%2C%20and%20Video.jpg";

const benefits = [
  'Real-time news aggregation from 50+ trusted sources',
  'AI-assisted summarization with key bullet points',
  'Multi-language support: Hindi, English (US/UK)',
  'Auto-refresh every 30 minutes',
  'Mobile-first responsive design',
  'SEO optimized for Google indexing',
];

const stats = [
  { icon: Users, value: '10K+', label: 'Active Traders' },
  { icon: Globe, value: '4', label: 'Languages' },
  { icon: Zap, value: '24/7', label: 'Live Updates' },
  { icon: Shield, value: '100%', label: 'Free Access' },
];

const AboutSection = () => {
  return (
    <section id="about" className="py-20 bg-background overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image Side */}
          <div className="relative order-2 lg:order-1">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={BULL_BEAR_IMAGE}
                alt="Bull and Bear Market"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/50 to-transparent"></div>
            </div>
            
            {/* Floating Stats Card */}
            <div className="absolute -bottom-8 -right-4 md:right-8 bg-card rounded-xl shadow-xl p-6 max-w-xs">
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="w-10 h-10 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-2">
                      <stat.icon className="w-5 h-5 text-primary" />
                    </div>
                    <p className="text-xl font-bold text-foreground">{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Decorative Element */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-primary/10 rounded-full blur-xl"></div>
          </div>

          {/* Content Side */}
          <div className="order-1 lg:order-2">
            <p className="section-subtitle text-left mb-2">About TradePulse</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6 text-left font-display">
              Your Trusted Source for Market Intelligence
            </h2>
            
            <p className="text-muted-foreground mb-6 leading-relaxed">
              TradePulse is a free, legal, auto-updating trading news platform that delivers 
              AI-assisted summaries of the latest stock market news. We aggregate content from 
              trusted global sources and present it in an easy-to-digest format with headlines, 
              subtitles, and bullet points.
            </p>

            <p className="text-muted-foreground mb-8 leading-relaxed">
              Whether you&apos;re tracking Indian markets (NSE/BSE) or global exchanges (NYSE, NASDAQ, 
              London Stock Exchange), TradePulse keeps you informed in your preferred language.
            </p>

            {/* Benefits List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {benefits.map((benefit) => (
                <div key={benefit} className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">{benefit}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <a href="#contact">
              <Button className="btn-primary group">
                Learn More About Us
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
